import React from 'react';
import type { BCPSection } from '../types/bcp';
import { BCP_FIXED_SECTIONS } from '../constants/bcpFixedSections';

export const BCP_SECTIONS: BCPSection[] = [
  // 簡易版-1. 基本情報と事業継続方針の策定
  {
    id: 'companyInfo',
    title: '1-1. 企業の基本情報',
    description: '企業名、所在地、従業員数、事業内容を入力してください',
    required: true,
    placeholder: '企業名：\n所在地：\n従業員数：\n事業内容：',
    minLength: 10,
    maxLength: 1000,
    minHeight: 'small'
  },
  {
    id: 'continuityPolicy',
    title: '1-2. 事業継続の基本方針',
    description: '事業継続における基本方針を記載してください',
    required: true,
    placeholder: '安全確保方針：\n早期復旧方針：\n社会的責任：',
    minLength: 10,
    maxLength: 1000,
    minHeight: 'medium'
  },

  // 簡易版-2. 想定リスクと影響分析
  {
    id: 'riskAnalysis',
    title: '2-1. 想定されるリスク',
    description: '想定される災害やリスクを具体的に記載してください',
    required: true,
    placeholder: '1. 自然災害（地震、台風、洪水等）\n2. 人為的災害（火災、爆発等）\n3. 停電・インフラ障害\n4. パンデミック\n5. サイバー攻撃\n6. その他',
    minLength: 20,
    maxLength: 2000,
    minHeight: 'large'
  },

  // 簡易版-3. 重要業務の特定と復旧計画
  {
    id: 'criticalBusinesses',
    title: '3-1. 重要事業・目標復旧時間・必要資源',
    description: '重要事業の特定、復旧目標時間と必要な資源を設定してください',
    required: true,
    placeholder: '【第一優先事業】\n事業名: \n選定理由: \n目標復旧時間（RTO）: \n必要資源:\n・人員（）\n・設備（）\n・原材料（）\n・電力・通信\n・基幹システム（）\n\n【第二優先事業】\n事業名: \n選定理由: \n目標復旧時間（RTO）: \n必要資源:\n・人員（）\n・設備（）\n・原材料（）\n・電力・通信\n・基幹システム（）',
    minLength: 10,
    maxLength: 1200,
    minHeight: 'large',
    guide: {
      content: (
        <div className="space-y-2">
          <p className="font-medium" style={{ color: '#0D357F', fontSize: '14px', lineHeight: '20px' }}>重要事業・復旧計画の考え方</p>
          <div style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
            <p>1.重要事業の特定</p>
            <p>事業を中断すると最も影響が大きい業務を特定してください。以下の観点から評価することが重要です。</p>
            <ul className="ml-4 space-y-1">
              <li>・自社の売上への影響</li>
              <li>・顧客や社会からの信頼・評判への影響</li>
              <li>・法的義務・契約上の責務</li>
              <li>・他の業務への波及効果</li>
            </ul>
            <p className="mt-2">2.目標復旧時間の設定</p>
            <p>事業中断時にどのくらいの時間で重要事業を復旧させるかを定めます。「顧客への迷惑」「社会全体への迷惑」「顧客離れの可能性」「財務影響」に大きな影響が出始める期間を設定します。</p>
            <p className="mt-2">3.必要な経営資源の整理</p>
            <p>重点事業の遂行は現地復旧が原則となります。重点事業を遂行するのに必要な経営資源を「人」「モノ」「資金」「情報」等の観点から入力しましょう。</p>
          </div>
        </div>
      ),
      example: (
        <div className="space-y-2">
          <p className="font-medium" style={{ color: '#0D357F', fontSize: '14px', lineHeight: '20px' }}>記載例</p>
          <div className="border border-gray-300 rounded p-3 bg-white" style={{ fontSize: '14px', lineHeight: '20px', color: '#4B5563' }}>
            <p>【第一優先事業】</p>
            <p>事業名: 電子機器筐体の製造</p>
            <p>選定理由: 売上の40%を占める主力製品で、取引先との契約上の納期厳守義務がある</p>
            <p>目標復旧時間（RTO）: 1週間以内</p>
            <p>必要資源:</p>
            <p>・人員（製造担当、品質管理担当）</p>
            <p>・設備（プレス機、加工機、組立ライン）</p>
            <p>・原材料（板金、樹脂部材）</p>
            <p>・電力・通信</p>
            <p>・基幹システム（生産管理・顧客管理）</p>
            <p className="mt-2">【第二優先事業】</p>
            <p>事業名: 産業機械部品の製造</p>
            <p>選定理由: 売上の35%を占め、安定した収益源である</p>
            <p>目標復旧時間（RTO）: 2週間以内</p>
            <p>必要資源:</p>
            <p>・人員（加工技術者、検査担当）</p>
            <p>・設備（専用工作機械、測定器）</p>
            <p>・原材料（鋼材、部品）</p>
            <p>・物流手段（保管倉庫、輸送車両）</p>
            <p>・システム（在庫管理、受発注管理）</p>
          </div>
        </div>
      ),
    },
  },
  {
    id: 'financialPlan',
    title: '3-2. 資金計画の考え方',
    description: '緊急時に必要な資金計画を記載してください',
    required: true,
    placeholder: '必要資金：\n・緊急対応費用：\n・復旧費用：\n・運転資金：\n\n資金調達方法：\n・現金・預金：\n・融資：\n・保険金：\n\n保険の活用：\n・火災保険：\n・事業継続保険：\n・その他：',
    minLength: 10,
    maxLength: 800,
    minHeight: 'medium'
  },
  {
    id: 'stockpilePlan',
    title: '3-3. 備蓄計画',
    description: '緊急時に必要な備蓄品と数量を記載してください',
    required: true,
    placeholder: '備蓄品リスト：\n・飲料水：\n・非常食：\n・衛生用品：\n・燃料：\n・医薬品：\n・その他必要物資：\n\n保管場所：\n管理責任者：',
    minLength: 10,
    maxLength: 600,
    minHeight: 'small',
    guide: {
      content: (
        <div className="space-y-2">
          <p className="font-medium" style={{ color: '#0D357F', fontSize: '14px', lineHeight: '20px' }}>補助金／助成金の活用</p>
          <div style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
            <p>備蓄物資の購入に補助金／助成金を活用できる場合があるので、必要に応じて事前に確認しましょう。</p>
            <p>
              参考ページ： <a
                href="https://mirasapo-plus.go.jp/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                https://mirasapo-plus.go.jp/
              </a>
            </p>
          </div>
        </div>
      ),
      example: <div></div>,
    },
  },

  // 簡易版-4. 現地復旧以外の代替手段
  {
    id: 'alternativeMeasures',
    title: '4-1. 現地復旧以外の代替策',
    description: '緊急時の代替手段を具体的に記載してください',
    required: true,
    placeholder: '代替手段：\n・リモートワーク体制：\n・クラウドサービス活用：\n・代替拠点：\n・代替取引先：\n・代替輸送手段：',
    minLength: 10,
    maxLength: 800,
    minHeight: 'medium',
    guide: {
      content: (
        <div className="space-y-2">
          <p className="font-medium" style={{ color: '#0D357F', fontSize: '14px', lineHeight: '20px' }}>代替策の考え方</p>
          <p style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
            重要事業において現地復旧戦略では BCP 目標が達成できない場合に、とりうる現地復旧戦略以外の戦略を策定してください。
            <br />
            戦略の例として「代替戦略(他拠点)」「在庫活用」「外部委託」「外部購入」「撤退・縮小」などがあります。
          </p>
        </div>
      ),
      example: (
        <div className="space-y-2">
          <p className="font-medium" style={{ color: '#0D357F', fontSize: '14px', lineHeight: '20px' }}>記載例</p>
          <div className="border border-gray-300 rounded p-3 bg-white" style={{ fontSize: '14px', lineHeight: '20px', color: '#4B5563' }}>
            <p>【代替拠点】</p>
            <p>・主要拠点の復旧に時間を要する場合、別拠点で同等の業務を実施する。</p>
            <p>・流用可能な設備はそのまま使用し、特有の設備は必要に応じて移設・手配する。</p>
            <p className="mt-2">【在庫活用】</p>
            <p>・重要製品の供給を継続するため、在庫を事前に積み増ししておく。</p>
            <p>・複数の倉庫に分散保管し、リスクを低減する。</p>
            <p className="mt-2">【外部委託】</p>
            <p>・有事には外部の協力会社に生産・業務を委託する。</p>
            <p>・事前協定を結び、必要に応じて自社スタッフを派遣して技術・運用面を支援する。</p>
            <p className="mt-2">【在宅勤務体制】</p>
            <p>・管理部門が災害時でも業務を継続できるよう、在宅勤務環境を整備する。</p>
            <p>・VPNやクラウドサービスなどのITインフラを導入する。</p>
          </div>
        </div>
      ),
    },
  },

  // 簡易版-5. BCP発動基準
  {
    id: 'activationCriteria',
    title: '5-1. BCP発動の判断基準',
    description: 'BCP発動の判断基準を記載してください（デフォルト内容を編集可能です）',
    required: true,
    placeholder: BCP_FIXED_SECTIONS.ACTIVATION_CRITERIA_5_1,
    minLength: 10,
    maxLength: 600,
    minHeight: 'small',
    readOnly: false
  },

  // 簡易版-6. 緊急時の組織体制
  {
    id: 'emergencyOrganization',
    title: '6-1. 緊急対応フロー（初動対応）',
    description: '緊急時の初動対応手順を記載してください',
    required: true,
    placeholder: '初動対応フロー：\n1. 安全確認\n2. 被害状況確認\n3. 緊急対策本部設置\n4. 関係者への連絡\n5. 応急復旧作業\n6. 事業継続判断',
    minLength: 10,
    maxLength: 800,
    minHeight: 'medium'
  },
  {
    id: 'rolesResponsibilities',
    title: '6-2. 対策本部の立ち上げ',
    description: '緊急時の組織体制と各担当者の役割を記載してください',
    required: true,
    placeholder: '本部長:{役職名を入力}\n\n【初期対応チーム】\n・メンバー:{役職名を入力}\n・役割:安否情報集約、各設備の被害状況集約、従業員の対応計画、負傷者対応\n\n【復旧対応チーム】\n・メンバー:{役職名を入力}\n・役割:重点事業の実施体制検討、システム状況対応、資金繰り管理、各種情報収集(災害情報、協力会社、他社の状況等)、情報発信',
    minLength: 10,
    maxLength: 800,
    minHeight: 'medium'
  },
  {
    id: 'communicationMethods',
    title: '6-3. 緊急時の連絡手段',
    description: '緊急時の連絡体制と手段を記載してください',
    required: true,
    placeholder: '連絡手段を入力してください',
    minLength: 10,
    maxLength: 600,
    minHeight: 'small'
  },
  // 簡易版-7. 事業継続に向けたその他の取組みなど
  {
    id: 'regularTraining',
    title: '7-1. 定期訓練',
    description: '定期的に実施する訓練内容を記載してください',
    required: true,
    placeholder: '定期訓練計画：\n・避難訓練：\n・情報共有訓練：\n・業務復旧シミュレーション：\n・緊急連絡訓練：\n\n実施頻度：\n評価方法：',
    minLength: 10,
    maxLength: 600,
    minHeight: 'small'
  },
  {
    id: 'bcpReview',
    title: '7-2. BCPの定期見直し',
    description: 'BCPの定期的な見直しと更新ルールを記載してください',
    required: true,
    placeholder: '見直しルール：\n・見直し頻度：\n・見直し担当者：\n・見直し項目：\n・改善策の反映方法：\n・最新リスクの評価：',
    minLength: 10,
    maxLength: 600,
    minHeight: 'small'
  },
  {
    id: 'employeeEducation',
    title: '7-3. 従業員教育',
    description: '従業員への教育・研修計画を記載してください',
    required: true,
    placeholder: '従業員教育計画：\n・新入社員研修：\n・定期研修：\n・緊急時行動指針：\n・教育資料：\n・理解度確認方法：',
    minLength: 10,
    maxLength: 600,
    minHeight: 'small'
  }
];
